import { Component, HostListener, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { User } from '../../models/User';
import { UserProfile } from '../../models/UserProfile';
import { MenuItem, SidenavService } from '../../services/sidenav/sidenav.service';
import { IAppState } from '../../store/interfaces/IAppState';
import {
  selectIsLogged,
  selectSidenavOpened,
  selectTenantNome,
  selectUser,
  selectUserProfile,
  selectUserType,
} from '../../store/selectors';
import * as FontAwesome from '@fortawesome/free-solid-svg-icons';
import { MediaService } from '../../services/media';
import { closeSidenav } from '../../store/actions';

interface ProfileContextViewModel {
  highlightedName: string;
  companyName: string | null;
  showManageProfiles: boolean;
  isProfileUser: boolean;
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
})
export class SidenavComponent {
  @ViewChild('drawer') drawer!: MatDrawer;

  user!: User;

  fontAwersome = FontAwesome;

  activatedItem: MenuItem | null = null;

  menuItems$ = this.sidenavService.menuItems$;

  isLoggedIn$ = this.store.select(selectIsLogged).pipe(tap(() => (this.activatedItem = null)));

  loggedUser$ = this.store.select(selectUser);

  userProfile$ = this.store.select(selectUserProfile);

  userType$ = this.store.select(selectUserType);

  profileContext$ = combineLatest([this.loggedUser$, this.userProfile$, this.userType$]).pipe(
    map(([user, userProfile, userType]) => this.getProfileContext(user, userProfile, userType))
  );

  sidenavOpened$ = this.store.select(selectSidenavOpened);

  smallScreen$ = this.mediaService.smallScreen$;

  tenantNome$ = this.store.select(selectTenantNome);

  constructor(
    private store: Store<IAppState>,
    private sidenavService: SidenavService,
    private mediaService: MediaService
  ) {}

  @HostListener('click', ['$event.target'])
  onClick(element: EventTarget | null) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const hasBackdropClass = element.classList.contains('mat-drawer-backdrop');

    if (hasBackdropClass) {
      this.store.dispatch(closeSidenav());
    }
  }

  toggleActivatedMenu(item: MenuItem): void {
    item.onClick?.();

    if (this.isMenuActivated(item)) {
      this.clearActivatedMenu();
      return;
    }

    this.activatedItem = item;
  }

  clearActivatedMenu(): void {
    this.activatedItem = null;

    this.closeSidenavIfSmallScreen();
  }

  isMenuActivated(item: MenuItem): boolean {
    return this.activatedItem === item;
  }

  getItemStyle(item: MenuItem): { [key: string]: string } {
    const length = item.children?.length ?? 0;

    if (this.isMenuActivated(item)) {
      return { 'max-height': `${length * 36}px` };
    }

    return {};
  }

  private closeSidenavIfSmallScreen(): void {
    this.smallScreen$
      .pipe(
        take(1),
        filter((v) => !!v)
      )
      .subscribe(() => {
        this.store.dispatch(closeSidenav());
      });
  }

  private getProfileContext(
    user: User | undefined,
    userProfile: UserProfile | undefined,
    userType: string | null
  ): ProfileContextViewModel {
    const isPJ = userType === 'PJ';
    const hasBoth = !!user && !!userProfile;
    const isCpfOrCnpjLogin = hasBoth && userProfile!.id === 0 && !String(userProfile!.nome || '').trim();

    if (isCpfOrCnpjLogin) {
      return {
        highlightedName: user?.nome || '',
        companyName: null,
        showManageProfiles: true,
        isProfileUser: false,
      };
    }

    const isSameUser = hasBoth && String(user!.id) === String(userProfile!.id);
    const isAccessProfile = isPJ && hasBoth && !isSameUser;

    return {
      highlightedName: isAccessProfile ? userProfile!.nome : user?.nome || '',
      companyName: isAccessProfile ? user!.nome : null,
      showManageProfiles: false,
      isProfileUser: isAccessProfile,
    };
  }
}
